import { fitTitle, fitDescription } from "@/lib/seo-text";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { Link, createFileRoute } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Can a Queensland estate be handled without meeting the lawyer?",
    a: "Yes. Instructions are taken by phone or video, identity is verified by video, and the application is filed electronically in the Supreme Court of Queensland. The only physical step is the original signed will, which is posted to us and held in safe custody until the grant issues.",
  },
  {
    q: "Does it matter that you are not in my town?",
    a: "No. Probate is a Supreme Court of Queensland matter, not a local court matter, so the executor's location has no bearing on the application. What matters is that the affidavit material is right the first time, because registry requisitions are the main cause of delay.",
  },
  {
    q: "Is remote probate cheaper?",
    a: "It should be, because there is no travel and no double handling. We charge a fixed professional fee quoted in writing before we start, with the court filing fee and the published notice at cost. We never charge a percentage of the estate.",
  },
  {
    q: "Who actually does the work?",
    a: `Michael Klein (admitted 2003) and Erin Wilkinson run every estate personally. You deal with the solicitor doing the work, not a case manager. Call ${FIRM.phone} to speak to one of them.`,
  },
];

const DESCRIPTION =
  "Grants of probate and letters of administration for executors anywhere in Queensland — Cairns to Toowoomba — handled online on a fixed fee, with no office visit and no travel charge.";
const TITLE = `Remote Probate Queensland | Fixed Fee, No Office Visit — ${FIRM.name}`;

export const Route = createFileRoute("/remote-probate/")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/remote-probate" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/remote-probate" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: RemoteProbateIndex,
});

function RemoteProbateIndex() {
  return (
    <>
      <PageHero
        eyebrow="Probate anywhere in Queensland"
        title="Remote probate — the whole estate, handled online"
        lead="You do not need a probate lawyer in your town. A grant is issued by the Supreme Court of Queensland state-wide, so we act for executors from Mount Isa to the Gold Coast on a fixed fee, entirely by phone, email and video."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Where we act</h2>
        <p className="measure mt-3 text-sm leading-relaxed text-muted-foreground">
          Everywhere in Queensland. A grant of probate is issued by the Supreme Court of Queensland,
          so it makes no difference whether the executor is in Cairns, Townsville, Rockhampton,
          Mackay, Toowoomba, Bundaberg, Gladstone, Mount Isa or overseas — the application is the
          same one and it is filed electronically. If your town is not on that list we still act.
          Call {FIRM.phone}.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Ready to start?{" "}
          <Link to="/executor-intake" className="font-semibold text-primary underline">
            Complete the executor intake
          </Link>{" "}
          and we'll confirm whether a grant is needed and the fixed fee, in writing.
        </p>

        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          Executor in Brisbane or elsewhere in Queensland?{" "}
          <Link to="/probate-solicitor-brisbane" className="font-semibold text-primary underline">
            See our probate solicitor page for Brisbane and QLD executors
          </Link>
          .
        </p>

        <div className="mt-8">
          <ExecutorIntakeForm heading="Start a probate enquiry" />
        </div>
      </section>

      <CtaBand
        title="Start the estate from wherever you are"
        body={`Call ${FIRM.phone} or send the intake — we come back the same or next business day.`}
      />
    </>
  );
}
